import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { lastWorkspace } from '../lib/storage';
import type { OrgSummary, Role } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchOrgs } from '../queries/orgs';

/**
 * Which library the dashboard and the recorder are pointed at.
 *
 * `null` is the personal library, which every account has and which is free
 * forever. Anything else is an org the API says the caller is an active member
 * of. The role comes back with it, and every role-aware surface reads it from
 * here rather than guessing.
 */

export interface WorkspaceContextValue {
  orgs: OrgSummary[];
  loading: boolean;
  error: unknown;
  /** Null = personal library. */
  activeOrg: OrgSummary | null;
  activeRole: Role | null;
  select: (orgId: string | null) => void;
  refetch: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(() => lastWorkspace.get());

  const query = useQuery({
    queryKey: qk.orgs(),
    queryFn: fetchOrgs,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const orgs = useMemo(() => query.data ?? [], [query.data]);

  // A remembered org that the user has since been removed from must not pin
  // the UI to a workspace the API will 403 on.
  const activeOrg = useMemo(
    () => orgs.find((org) => org.id === selectedId) ?? null,
    [orgs, selectedId],
  );

  const select = useCallback((orgId: string | null) => {
    setSelectedId(orgId);
    lastWorkspace.set(orgId);
  }, []);

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      orgs,
      loading: query.isLoading,
      error: query.error,
      activeOrg,
      activeRole: activeOrg?.role ?? null,
      select,
      refetch,
    }),
    [activeOrg, orgs, query.error, query.isLoading, refetch, select],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspace must be used inside <WorkspaceProvider>.');
  return value;
}
